import { Box, LinearProgress, Stack, Typography } from '@mui/material'

type ProgressStepProps = {
	name: string
	status: 'completed' | 'working' | 'waiting'
	step: number
}

/**
 * A React component that represents a single step in a progress indicator.
 * It displays the step's name, its current status, and a progress bar.
 *
 * @component
 * @param {string} name - The name of the step to be displayed.
 * @param {'completed' | 'working' | 'waiting'} status - The current status of the step.
 *   - `'completed'`: The step is finished.
 *   - `'working'`: The step is currently in progress.
 *   - `'waiting'`: The step is yet to be started.
 * @param {number} step - The step number in the sequence, used to calculate animation delays.
 * @example
 * <ProgressStep name="Step 1" status="working" step={1} />
 */
export default function ProgressStep({ name, status, step }: ProgressStepProps) {
	return (
		<Box flex={1}>
			<Stack gap={1} alignItems="center">
				<Typography>{name}</Typography>
				<LinearProgress
					sx={{
						height: '1rem',
						width: '100%',
						'& .MuiLinearProgress-bar': {
							transitionDelay: `${(step - 1) * 200}ms`,
							transitionDuration: '200ms',
							animationDelay: `${(step - 1) * 200}ms`,
						},
					}}
					variant={status === 'working' ? 'indeterminate' : 'determinate'}
					value={status === 'completed' ? 100 : 0}
				/>
			</Stack>
		</Box>
	)
}
